import { useEffect, useState } from "react";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { Col, Row } from "react-bootstrap";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import InputWithIcon from "../../../components/shared/fields/InputWithIcon";
import Button from "../../../components/shared/buttons/button";
import { RefreshSvg } from "../../../svgFiles/RefreshSvg";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import { useDistributeRawMaterialMutation } from "../unloading/unloadingApi";
import toast from "react-hot-toast";
import useTranslator from "../../../hooks/useTranslator";
import { useGetPalletsQuery } from "../../../app/globalApi";
import { useGetRoomsQuery } from "../../ripening/ripeningRooms/ripeningApi";
import moment from "moment";
import PalletCard from "./PalletCard";
import {
  calculateGreenBoxes,
  calculateOrangeBoxes,
  getAreaLabel,
  getPalletAreaLabel,
  getSelectedArea,
  getTotalPalletKg,
} from "./palletUtils";
import {
  EMPTY_ORANGE_CASE_WEIGHT,
  PLASTIC_EMPTY_PALLET_WEIGHT,
  GREEN_CASE_WEIGHT,
  ORANGE_CASE_FRUIT_WEIGHT,
  PALLET_ORANGE_CASES,
  WASHING_PALLETIZE_PALLET_MAX_WEIGHT,
  WASHING_PALLETIZE_PALLET_MIN_WEIGHT,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATETIME_FORMAT,
  MARGIN_FOR_REJECTED_KG,
} from "../../../helpers/constants";
import { ceilToTwoDecimal, convertToUserTimezone, formatDateAndTime, getCurrentDateTime } from "../../../helpers/Utils";

const PALLET_TOTAL_MIN_WEIGHT = ceilToTwoDecimal(
  PALLET_ORANGE_CASES * EMPTY_ORANGE_CASE_WEIGHT +
    PLASTIC_EMPTY_PALLET_WEIGHT +
    PALLET_ORANGE_CASES * ORANGE_CASE_FRUIT_WEIGHT
);

const PALLET_TOTAL_MAX_WEIGHT = ceilToTwoDecimal(
  PALLET_TOTAL_MIN_WEIGHT + 4000
);

let interval = null;

const margin_for_rejected_kg = parseFloat(MARGIN_FOR_REJECTED_KG);

const UpdateWashingModal = ({ show, setShow, unloading, mode }) => {
  // Today
  const today = getCurrentDateTime("YYYY-MM-DDTHH:mm");
  
  const washedReadyAt =  unloading?.washed_ready_at
    ? convertToUserTimezone(unloading?.washed_ready_at)
    : "";

  // Initial Form Data
  const initialFormData = {
    kg_after_wash: "",
    rejected_case_count: "",
    rejected_case_count_kg: "",
    total_rejected_kg: "",
    total_rejected_kg_actual : "",
    status: "",
    pallets: [],
    washed_done_at: washedReadyAt,
  };

  // RTK Query
  const [distributeRawMaterial, { isLoading: isUpdating }] =
    useDistributeRawMaterialMutation();
  const { data: pallets, refetch: refetchPallets } = useGetPalletsQuery();
  const { data: roomData } = useGetRoomsQuery();

  // States
  const [formData, setFormData] = useState(initialFormData);
  const [selectedPalletId, setSelectedPalletId] = useState("");
  const [selectedPalletTotalKg, setSelectedPalletTotalKg] = useState("");
  const [selectedPalletTotalCases, setSelectedPalletTotalCases] = useState("");
  const [selectedPalletAreaId, setSelectedPalletAreaId] = useState("");
  const [selectedPalletDate, setSelectedPalletDate] = useState("");
  const [allAreas, setAllAreas] = useState([]);
  const [dateLabel, setDateLabel] = useState("Date");
  const [expectedCases, setExpectedCases] = useState(0);
  const [localPallets, setLocalPallets] = useState([]);
  const [localFilteredPallets, setLocalFilteredPallets] = useState([]);
  const [inputErrors, setInputErrors] = useState({});
  const [inputMessages, setInputMessages] = useState({});

  // Translation
  const { _t } = useTranslator();
  const messages = {
    selectPallet: _t("please_select_a_pallet", "Please select a pallet."),
    validKg: _t(
      "please_enter_a_valid_pallet_kg",
      "Please enter a valid pallet weight in kg."
    ),
    validCases: _t(
      "please_enter_valid_pallet_cases",
      "Please enter valid number of cases (1–36)."
    ),
    onlyOneLowWeight: _t(
      "only_one_pallet_with_weight_less_than",
      "Only one pallet with weight less than"
    ),
    isAllowed: _t("is_allowed", "is allowed"),
    deleteExisting: _t(
      "please_delete_the_existing_one_first",
      "Please delete the existing one first."
    ),
    kgBetween: _t("pallet_kg_must_be_between", "Pallet weight must be between"),
    and: _t("and", "and"),
    kgTooLow: _t(
      "pallet_kg_entered_too_low",
      "The pallet weight is too low for the number of cases."
    ),
    negativeValue: _t("value_cannot_be_negative", "Values cannot be negative."),
    invalidNumber: _t(
      "please_enter_valid_number",
      "Please enter a valid number."
    ),
    kgLessOrEqual: _t(
      "pallet_kg_entered_must_be_equal_or_greater",
      "Pallet weight must be at least"
    ),
  };

  useEffect(() => {
    if (
      unloading.ripening_pallets &&
      Array.isArray(unloading.ripening_pallets) &&
      unloading.ripening_pallets.length > 0
    ) {
      
      const updatedFormData = {
        kg_after_wash: unloading.kg_after_wash,
        rejected_case_count: unloading.rejected_case_count,
        rejected_case_count_kg: unloading.rejected_case_count_kg,
        total_rejected_kg: unloading.rejected_case_count_kg,
        total_rejected_kg_actual : unloading.total_rejected_kg_actual,
        status: unloading.status,
        pallets: [],
        washed_done_at: unloading.washed_done_at
          ? convertToUserTimezone(unloading.washed_done_at,"YYYY-MM-DDTHH:mm")
          : "",
      };

      unloading.ripening_pallets.forEach((ripeningPallet) => {
        const oldPallet = {
          pallet_id: ripeningPallet.pallet_id,
          pallet_kg_entered: ripeningPallet.pallet_kg_entered,
          pallet_total_cases: ripeningPallet.pallet_total_cases,
          pallet_area: ripeningPallet.pallet_area,
          pallet_date: ripeningPallet.pallet_entered_date
            ? convertToUserTimezone(ripeningPallet.pallet_entered_date)
            : "",
          pallet_date_display :ripeningPallet.pallet_entered_date
            ? convertToUserTimezone(ripeningPallet.pallet_entered_date,DEFAULT_DATE_FORMAT)
            : "",
          pallet_room_id: ripeningPallet.pallet_room_id,
          pallet_room_label: ripeningPallet.pallet_area,
        };

        if (oldPallet.pallet_area === "cold_storage") {
          oldPallet.pallet_room_label = "Cold Storage";
        }
        if (oldPallet.pallet_area === "ripening") {
          oldPallet.pallet_room_label = `Ripening Room ${oldPallet.pallet_room_id}`;
          if (ripeningPallet.pallet_expected_date_to_exit) {
            oldPallet.pallet_date = convertToUserTimezone(ripeningPallet.pallet_expected_date_to_exit)
          }
        }
        if (oldPallet.pallet_area === "sorting") {
          oldPallet.pallet_room_label = "Sorting";
        }

        updatedFormData.pallets.push(oldPallet);
      });

      setFormData(updatedFormData);
    }
  }, [unloading.ripening_pallets]);

  useEffect(() => {
    if (pallets) {
      const updatedPallets = [...pallets.data];

      // If there are unloading pallets, update the original pallets
      if (unloading?.ripening_pallets?.length > 0) {
        unloading.ripening_pallets.forEach((unloadingPallet) => {
          // Find the corresponding pallet in the original data
          const originalPalletIndex = updatedPallets.findIndex(
            (p) => p.id === unloadingPallet.pallet_id
          );

          if (originalPalletIndex !== -1) {
            const newKg = Math.max(
              0,
              updatedPallets[originalPalletIndex].pallet_fruit_entered_in_kg -
                unloadingPallet.pallet_kg_entered
            );

            const newCases = Math.max(
              updatedPallets[originalPalletIndex].pallet_entered_cases -
                unloadingPallet.pallet_total_cases
            );
            // Update the found pallet
            updatedPallets[originalPalletIndex] = {
              ...updatedPallets[originalPalletIndex],
              pallet_fruit_entered_in_kg: newKg,
              pallet_entered_cases: newCases,
              pallet_status: "available",
            };
          }
        });
      }

      setLocalPallets(updatedPallets);
      setLocalFilteredPallets(updatedPallets);
    }
  }, [pallets, unloading?.pallets]);

  // All Areas Cold Storage, All Ripening Rooms, Sorting Area
  useEffect(() => {
    if (!roomData?.data) return;

    const areas = [
      {
        label: "Cold Storage",
        value: "cold_storage",
        cases_capacity: null,
        total_cases: 0,
        area: "cold_storage",
        total_fruit_kg: 0,
      },
      ...roomData.data.map((room) => ({
        label: `Ripening ${room.name}`,
        value: room.id,
        cases_capacity: room.cases_capacity,
        total_cases: room.total_cases,
        area: "ripening",
        total_fruit_kg: room.total_fruit_kg,
      })),
      {
        label: "Sorting Area",
        value: "sorting",
        cases_capacity: null,
        total_cases: 0,
        area: "sorting",
        total_fruit_kg: 0,
      },
    ];
    setAllAreas(areas);
  }, [roomData]);

  // Handle Select Area Change
  useEffect(() => {
    const area = getSelectedArea(selectedPalletAreaId, allAreas);
    if (area?.area === "ripening") {
      setDateLabel(_t("expected_ripening_date", "Expected Ripening Date"));
      let addDays = 3;
      const baseDate = formData.washed_done_at ?? getCurrentDateTime("YYYY-MM-DDTHH:mm");

      const dateToUse = moment(baseDate).add(addDays, "days");
      setSelectedPalletDate(dateToUse.format("YYYY-MM-DDTHH:mm"));
    } else {
      setSelectedPalletDate(today);
      setDateLabel(_t("date", "Date"));
    }

    const filteredPallets = localPallets?.filter((list) => {
      if (isPalletAvailable(list)) {
        return true;
      }
      return false;
    });

    setLocalFilteredPallets(filteredPallets);
  }, [selectedPalletAreaId]);

  // Handle Pallet Add
  useEffect(() => {
    const kgEnteredBefore = parseFloat(unloading?.kg_entered || 0);
    const palletsTotalKg = getTotalPalletKg(formData?.pallets || []);
    const remainingKg = ceilToTwoDecimal(kgEnteredBefore - palletsTotalKg);
    const rejectedKg = remainingKg > 0 ? remainingKg : 0;
    let rejectedCases = calculateGreenBoxes(rejectedKg);
    if (rejectedCases > unloading?.total_cases) {
      rejectedCases = unloading?.total_cases;
    }
    setFormData((prev) => ({
      ...prev,
      kg_after_wash: palletsTotalKg,
      rejected_case_count: rejectedCases,
      rejected_case_count_kg: rejectedKg,
      total_rejected_kg: rejectedKg,
      total_rejected_kg_actual:rejectedKg
    }));

   
  }, [formData?.pallets]);

  // Handle expected cases
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (selectedPalletTotalKg && selectedPalletTotalKg > 0) {
        setExpectedCases(calculateOrangeBoxes(selectedPalletTotalKg));
      } else {
        setExpectedCases(0);
      }
    }, 200);
    // Cleanup the previous timeout
    return () => clearTimeout(timeout);
  }, [selectedPalletTotalKg]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const kg = parseFloat(selectedPalletTotalKg);
      if (selectedPalletTotalKg && !isNaN(kg)) {
        if (kg >= PALLET_TOTAL_MIN_WEIGHT) {
          setSelectedPalletTotalCases(PALLET_ORANGE_CASES);
        } else {
          setSelectedPalletTotalCases("");
        }
      } else {
        setSelectedPalletTotalCases("");
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [selectedPalletTotalKg]);

  // Handlers Functions
  //============================================

  const calculateNewPallet = (kg, cases) => {
    const netKg =
      kg - (cases * EMPTY_ORANGE_CASE_WEIGHT + PLASTIC_EMPTY_PALLET_WEIGHT);
    if (netKg < 0) return null;

    const isPalletized = netKg >= WASHING_PALLETIZE_PALLET_MIN_WEIGHT;
    // let pallet_total_cases = isPalletized ? PALLET_ORANGE_CASES : cases; //Math.floor(netKg / ORANGE_CASE_FRUIT_WEIGHT);
    let pallet_total_cases = cases;
    if (pallet_total_cases > PALLET_ORANGE_CASES) {
      pallet_total_cases = PALLET_ORANGE_CASES;
    }

    return {
      pallet_kg_entered: netKg,
      pallet_total_cases: pallet_total_cases,
      pallet_type: isPalletized ? "palletize" : "non_palletize",
    };
  };

  
  const addRipeningHandler = () => {

    if (!selectedPalletAreaId) {
      toast.error(_t("please_enter_a_valid_area", "Please enter a valid storage area."));
      return;
    }

    if (!selectedPalletId) {
      toast.error(messages.selectPallet);
      return;
    }

    let kg = parseFloat(selectedPalletTotalKg);
    if (isNaN(kg)) {
      toast.error(messages.validKg);
      return;
    }

    let cases = parseInt(selectedPalletTotalCases);
    if (isNaN(cases) || cases <= 0 || cases > PALLET_ORANGE_CASES) {
      toast.error(messages.validCases);
      return;
    }

    if (kg <= 0) {
      toast.error(
        _t("please_enter_a_valid_pallet_kg", "Please enter a valid pallet KG")
      );
      return;
    }

    if (kg > PALLET_TOTAL_MAX_WEIGHT) {
      toast.error(_t("pallet_weight_cannot_exceed", `Pallet weight cannot exceed ${PALLET_TOTAL_MAX_WEIGHT} kg.`));
      return;
    }

    //---------------------------------------------------------------
    // validate weight against cases
    const minExpectedKg = ceilToTwoDecimal(
      cases * ORANGE_CASE_FRUIT_WEIGHT +
        cases * EMPTY_ORANGE_CASE_WEIGHT +
        PLASTIC_EMPTY_PALLET_WEIGHT
    );

    const maxExpectedKg = ceilToTwoDecimal(minExpectedKg + 4000);
    if (cases == PALLET_ORANGE_CASES || kg >= PALLET_TOTAL_MIN_WEIGHT) {
      if (kg < minExpectedKg || kg > maxExpectedKg) {
        toast.error(
          `${messages.kgBetween} ${minExpectedKg} ${messages.and} ${maxExpectedKg}`
        );
        return;
      }
    }
    //--------------------------------------------------------------
    const newPallet = calculateNewPallet(kg, cases);
    if (!newPallet) {
      toast.error(messages.kgTooLow);
      return;
    }

    kg = newPallet.pallet_kg_entered;
    cases = newPallet.pallet_total_cases;

    //--------------------------------------------------------------
    const localPallet = localPallets?.find(
      (localPallet) => localPallet.id == selectedPalletId
    );
    //--------------------------------------------------------------
    let kgEnteredBefore = parseFloat(unloading?.kg_entered || 0);
    if (localPallet) {
      kgEnteredBefore += localPallet.pallet_fruit_entered_in_kg;
    }
    const palletsTotalKg = getTotalPalletKg(formData?.pallets || []);
    let remainingKg = kgEnteredBefore - palletsTotalKg;
    remainingKg = Math.round(remainingKg * 100) / 100;
    if (remainingKg <= 0) {
      toast.error(_t("no_remaining_kg_left", "No weight remaining to distribute."));
      return;
    }

    if (kg > remainingKg) {
      toast.error(
        `${_t(
          "pallet_weight_must_be_less_than_or_equal_to_remaining_weight",
          "Pallet weight must be less than or equal to remaining weight"
        )} ${remainingKg} ${_t("kg", "Kg")}`
      );
      return;
    }
    //---------------------------------------------------------------
    const area = getSelectedArea(selectedPalletAreaId, allAreas);
    if (!selectedPalletDate) {
      toast.error(_t("please_enter_a_valid_date", "Please enter a valid date"));
      return;
    }
    //--------------------------------------------------------------
    if (cases < PALLET_ORANGE_CASES) {
      // filter pallets based on area and cases less than PALLET_ORANGE_CASES
      const filteredPallets = formData?.pallets?.filter((pallet) => {
        if (
          pallet.pallet_area === area.area &&
          pallet.pallet_total_cases < PALLET_ORANGE_CASES
        ) {
          const isAlreadyUsedPallet = localPallets?.find(
            (localPallet) =>
              localPallet.id == pallet.pallet_id &&
              localPallet.pallet_fruit_entered_in_kg > 0
          );
          if (isAlreadyUsedPallet) {
            return false;
          }
          return true;
        }
        return false;
      });

      if (filteredPallets.length >= 1) {
        toast.error(
          _t(
            "only_one_pallet_per_area_is_allowed_to_be_under",
            "Only one pallet per area is allowed to be under"
          ) + ` ${WASHING_PALLETIZE_PALLET_MIN_WEIGHT} ${_t("kg", "Kg")}`
        );
        return;
      }
    }
    //---------------------------------------------------------------
    if (area.area === "ripening") {
      const remaining_cases = area.cases_capacity - area.total_cases;
      if (cases > remaining_cases) {
        toast.error(
          `${_t(
            "pallet_has_left_only_capacity_of",
            "Pallet has left only capacity of"
          )} ${remaining_cases} ${_t("cases", "Cases")}`
        );
        return;
      }
    }
    //---------------------------------------------------------------

    //---------------------------------------------------------------

    if (localPallet.pallet_area) {
      if (kg < localPallet.pallet_fruit_entered_in_kg) {
        toast.error(
          _t("pallet_kg_must_exceed_existing", `This pallet already has ${localPallet.pallet_fruit_entered_in_kg} kg recorded. The new weight must be higher than the existing amount.`)
        );
        return;
      }
      kg = ceilToTwoDecimal(kg - localPallet.pallet_fruit_entered_in_kg);
      if (cases < localPallet.pallet_entered_cases) {
        toast.error(
          _t("cases_must_exceed_existing", `The number of cases must be greater than the already recorded ${localPallet.pallet_entered_cases} cases.`)
        );
        return;
      }
      if (cases + localPallet.pallet_entered_cases > PALLET_ORANGE_CASES) {
        cases = cases - localPallet.pallet_entered_cases;
      }
    }
    //---------------------------------------------------------------

    //---------------------------------------------------------------
    kg = Math.round(kg * 100) / 100;
    setFormData((prev) => ({
      ...prev,
      pallets: [
        ...prev.pallets,
        {
          pallet_id: selectedPalletId,
          pallet_kg_entered: kg,
          pallet_total_cases: cases,
          pallet_area: area.area,
          pallet_date: formatDateAndTime(selectedPalletDate,DEFAULT_DATETIME_FORMAT),
          pallet_room_id: area.value, // assuming area.value is the room id
          pallet_room_label: area.label, // only use to show in pallet list
        },
      ],
    }));

    //---------------------------------------------------------------
    if (area.area === "ripening") {
      setAllAreas((areas) =>
        areas.map((a) =>
          a.value === area.value
            ? {
                ...a,
                total_cases: a.total_cases + cases,
                total_fruit_kg: a.total_fruit_kg + kg,
              }
            : a
        )
      );
    }

    //---------------------------------------------------------------
    setSelectedPalletId("");
    setSelectedPalletTotalKg("");
    setSelectedPalletTotalCases("");
    setSelectedPalletAreaId("");
    setSelectedPalletDate("");
    //---------------------------------------------------------------
  };

  const deletePalletHandler = (removedPallet) => {
    const filteredPallets = formData.pallets.filter(
      (_p) => _p.pallet_id != removedPallet.pallet_id
    );
    setFormData((prev) => ({
      ...prev,
      pallets: filteredPallets,
    }));

    if (removedPallet.pallet_area === "ripening") {
      const updatedAllAreas = allAreas.map((area) => {
        if (
          area.area === "ripening" &&
          area.value === removedPallet.pallet_room_id
        ) {
          return {
            ...area,
            total_cases:
              area.total_cases - removedPallet.pallet_total_cases || 0,
            total_fruit_kg:
              area.total_fruit_kg - removedPallet.pallet_kg_entered || 0,
          };
        }
        return { ...area };
      });
      setAllAreas(updatedAllAreas);
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "rejected_case_count") {
      if (value === "" || value <= 0) {
        value = 0;
      } else {
        value = value;
      }
    } 
    if (name === "washed_done_at") {
      if (value == "") {
        value = washedReadyAt;
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOnBlur = (e) => {
    const { name, value } = e.target;
    let error = "";

    if (name === "total_rejected_kg") {
      const numericValue = parseFloat(value);
      const originalValue = parseFloat(formData.rejected_case_count_kg);

      const minAllowed = originalValue - originalValue * margin_for_rejected_kg;
      const maxAllowed = originalValue + originalValue * margin_for_rejected_kg;

      if (numericValue < minAllowed || numericValue > maxAllowed) {
   
        error = `Value must be within ±${margin_for_rejected_kg * 100}% range.`;

        setInputErrors((prev) => ({
          ...prev,
          [name]: error,
        }));

        setInputMessages((prev) => ({
          ...prev,
          [name]: "",
        }));

        setFormData((prev) => ({
          ...prev,
          [name]: originalValue,
        }));
      } else {
        const difference = numericValue - originalValue;
        const percentChange = (difference / originalValue) * 100;
        const roundedPercent = Math.abs(percentChange).toFixed(2);
        let message = "";

        if (percentChange > 0) {
          message = `Increased by ${roundedPercent}%`;
        } else if (percentChange < 0) {
          message = `Decreased by ${roundedPercent}%`;
        }

        setInputMessages((prev) => ({
          ...prev,
          [name]: message,
        }));

        setInputErrors((prev) => ({
          ...prev,
          [name]: "",
        }));

        setFormData((prev) => ({
          ...prev,
          [name]: numericValue,
        }));
      }
    }

    if(name === "total_rejected_kg_actual"){
      const actualVal = parseFloat(value);
      const expectedVal = parseFloat(formData.total_rejected_kg);

      let percentageChange = 0;

      if(  isNaN(actualVal) ||  actualVal === "" || actualVal === undefined || (actualVal === expectedVal)  ){
         return setInputMessages((prev) => ({
            ...prev,
            [name]: "",
         }));
      }

      if (expectedVal > 0) {
        percentageChange = ((actualVal - expectedVal) / expectedVal) * 100;
      } else {
        percentageChange = 0; // or handle zero case as needed
      }

      percentageChange = Math.round(percentageChange * 100) / 100; // 2 decimal places


      const message = percentageChange > 0 
                    ? `${percentageChange}% increase` 
                    : `${Math.abs(percentageChange)}% decrease`;

      setInputMessages((prev) => ({
        ...prev,
        [name]: message,
      }));

    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const totalRejectedKg = formData.total_rejected_kg;

    const finalData = {
      ...formData,
      rejected_case_count_kg: totalRejectedKg,
    };

    if (isUpdating) return;

    // check status
    if (finalData.status !== "washing_done")
      return toast.error(_t("please_select_status", "Please select a status."));

    // check status
    if (!finalData.washed_done_at)
      return toast.error(
        _t("please_select_washing_date", "Please select washing date")
      );

    // check if rejected_case_count is correct
    let rejectedCases = calculateGreenBoxes(finalData.rejected_case_count_kg);
    if (rejectedCases > unloading?.total_cases) {
      rejectedCases = unloading?.total_cases;
    }

    if (rejectedCases != finalData.rejected_case_count) {
      let minRejectedCases = rejectedCases > 0 ? rejectedCases - 1 : 0;
 
    }

    try {
      const payload = { id: unloading.id, requestBody: finalData };
      await distributeRawMaterial(payload).unwrap();
      refetchPallets();
      handleClose();
    } catch (error) {
   
    }
  };
  //============================================

  // Common Functions
  //============================================
  const handleClose = () => {
    setShow(false);
  };

  const isPalletAvailable = (pallet) => {
    const isBasicAvailable =
      pallet.pallet_status === "available" &&
      pallet.pallet_fruit_entered_in_kg < WASHING_PALLETIZE_PALLET_MAX_WEIGHT &&
      pallet.pallet_entered_cases < PALLET_ORANGE_CASES;

    if (!isBasicAvailable) return false;

    const area = pallet.pallet_area;
    const areaRoomId = pallet.pallet_area_room_id;

    let isAreaCompatible = false;

    switch (selectedPalletAreaId) {
      case "cold_storage":
        isAreaCompatible = !area || area === "cold_storage";
        break;
      case "sorting":
        isAreaCompatible = !area;
        break;
      default:
        isAreaCompatible =
          !area || (area === "ripening" && selectedPalletAreaId == areaRoomId);
    }

    if (!isAreaCompatible) return false;

    const alreadySelected = (formData.pallets || []).some(
      (p) => p.pallet_id == pallet.id
    );

    return !alreadySelected;
  };

  const palletLabel = (list) => {
    let str = `${list.pallet_name}`;
    if (list.pallet_fruit_entered_in_kg > 0) {
      str += ` (${list.pallet_fruit_entered_in_kg} ${_t("kg")} / ${
        list.pallet_entered_cases
      } ${_t("cases")})`;
    }
    return str;
  };
  //============================================

  const renderFormFields = () => (
    <>
      <Col lg={6}>
        <InputWithLabel
          label={_t("batch_code", "Batch Code")}
          type="text"
          name="batch_code"
          value={unloading?.batch_code}
          disabled={true}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("kg_before_wash", "KG Before Wash")}
          type="text"
          name="kg_entered"
          value={unloading?.kg_entered}
          disabled={true}
          placeholder={_t("kg_before_wash", "KG Before Wash")}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("total_cases_entered", "Total Cases Entered")}
          type="text"
          name="total_cases"
          value={unloading?.total_cases}
          placeholder={_t("total_cases_entered", "Total Cases Entered")}
          disabled={true}
        />
      </Col>
      <Col lg={6}>
        <Row>
          <Col lg={6} className="pe-1">
            <InputWithLabel
              label={_t("palletized_cases", "Palletized Cases")}
              placeholder={_t("palletized_cases", "Palletized Cases")}
              type="text"
              name="palletize_cases"
              value={unloading?.palletize_cases}
              disabled={true}
            />
          </Col>
          <Col lg={6} className="ps-1">
            <InputWithLabel
              label={_t("non_palletized_cases", "Non Palletized Cases")}
              placeholder={_t("non_palletized_cases", "Non Palletized Cases")}
              type="text"
              name="non_palletize_cases"
              value={unloading?.non_palletize_cases}
              disabled={true}
            />
          </Col>
        </Row>
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("kg_after_wash", "KG After Wash")}
          type="number"
          name="kg_after_wash"
          value={formData.kg_after_wash}
          placeholder={"0"}
          disabled={true}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("washed_date", "Washing Date")}
          type="datetime-local"
          name="washed_done_at"
          value={formData.washed_done_at}
          placeholder={_t("washed_date", "Washing Date")}
          onChange={handleChange}
          min={washedReadyAt}
          disabled={true}
        />
      </Col>
      <Col lg={12}>
        <div className="d-flex gap-2 align-items-center">
          <div className="w-100">
            <SelectBox
              label={_t("select_area", "Select Area")}
              name="pallet_id"
              options={allAreas.map((list) => ({
                value: list.value,
                label: getAreaLabel(list, _t),
                disabled:
                  list.area === "ripening" &&
                  list.cases_capacity - list.total_cases < expectedCases
                    ? true
                    : false,
              }))}
              value={selectedPalletAreaId}
              onChange={(e) => {
                setSelectedPalletAreaId(e.target.value);
              }}
            />
          </div>
          <div className="w-100">
            <SelectBox
              label={_t("select_pallet", "Select Pallet")}
              name="pallet_id"
              options={localFilteredPallets?.map((list) => ({
                value: list.id,
                label: palletLabel(list),
                // disabled: !isPalletAvailable(list),
              }))}
              value={selectedPalletId}
              required={false}
              onChange={(e) => {
                setSelectedPalletId(e.target.value);
              }}
              disabled={!selectedPalletAreaId}
            />
          </div>
          <div className="w-100">
            <InputWithIcon
              label={_t("total_pallet_kg", "Total Pallet KG")}
              type="number"
              name="total_pallet_kg"
              icon={RefreshSvg}
              value={selectedPalletTotalKg}
              onChange={(e) => {
                setSelectedPalletTotalKg(e.target.value);
              }}
              placeholder={"0"}
              className={"cmn_input"}
            />
          </div>
          <div className="w-100">
            <SelectBox
              label={_t("total_pallet_cases", "Total Pallet Cases")}
              name="total_pallet_cases"
              value={selectedPalletTotalCases}
              options={Array.from({ length: 36 }, (_, i) => ({
                value: (36 - i).toString(),
                label: (36 - i).toString(),
              }))}
              onChange={(e) => setSelectedPalletTotalCases(e.target.value)}
            />
          </div>
          <div className="w-100">
            <InputWithLabel
              label={_t(dateLabel, dateLabel)}
              type="datetime-local"
              name="date"
              value={selectedPalletDate}
              onChange={(e) => {
                setSelectedPalletDate(e.target.value);
              }}
              min={today}
              disabled={
                selectedPalletAreaId == "sorting" ||
                selectedPalletAreaId == "cold_storage"
              }
            />
          </div>
          <Button
            label={
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.808 11.58L4.796 0.948H6.752L6.764 11.58H4.808ZM0.452 7.26V5.304L11.084 5.292V7.248L0.452 7.26Z"
                  fill="white"
                />
              </svg>
            }
            size="small"
            onClick={addRipeningHandler}
            isLoading={isUpdating}
            className={"rounded-circle circle_btn"}
          />
        </div>
      </Col>
      <Col xs={12}>
        <div className="pallets_list">
          <h6>{_t("added_pallets", "Added Pallets")}</h6>
          {["cold_storage", "ripening", "sorting"].map((area) => (
            <PalletCard
              key={area}
              area={area}
              allAreas={allAreas}
              pallets={formData.pallets}
              onDelete={deletePalletHandler}
              label={_t(area, getPalletAreaLabel(area))}
            />
          ))}
        </div>
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("expected_total_rejected_kg", "Total Rejected Kg (Expected)")}
          type="text"
          name="total_rejected_kg"
          // value={formData.rejected_case_count_kg}
          value={formData.total_rejected_kg}
          placeholder={_t("expected_total_rejected_kg", "Total Rejected Kg (Expected)")}
          disabled={true}
          error={inputErrors["total_rejected_kg"]}
          message={inputMessages["total_rejected_kg"]}
          onChange={handleChange}
          onBlur={handleOnBlur}
        />
      </Col>
      <Col lg={6}>
        <InputWithIcon
          label={_t("total_rejected_kg", "Total Rejected Kg (Actual)")}
          type="number"
          name="total_rejected_kg_actual"
          icon={RefreshSvg}
          value={formData.total_rejected_kg_actual || 0}
          placeholder={_t("total_rejected_kg", "Total Rejected Kg (Actual)")}
          disabled={false}
          onChange={handleChange}
          onBlur={handleOnBlur}
          message={inputMessages["total_rejected_kg_actual"]}
          className={"cmn_input"}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("total_rejected_cases", "Total Rejected Cases")}
          type="number"
          name="rejected_case_count"
          value={formData.rejected_case_count}
          placeholder={"0"}
          onChange={handleChange}
          disabled={formData?.rejected_case_count_kg == 0}
        />
      </Col>
    
      <Col lg={6}>
        <SelectBox
          label={_t("status", "Status")}
          name="status"
          value={formData.status}
          options={[
            {
              value: "washing_done",
              label: _t("washing_done", "Washing Done"),
            },
          ]}
          onChange={handleChange}
        />
      </Col>
    </>
  );

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={_t("update_washing_data", "Update Washing Data")}
      size={"xl"}
    >
      <Row>{renderFormFields()}</Row>
      <Row>
        <Col className="text-end">
          <Button
            label={_t("save_changes", "Save Changes")}
            size="small"
            onClick={handleSubmit}
            disabled={isUpdating}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default UpdateWashingModal;
